'use client';

import { c as _c } from "react/compiler-runtime";
import type { FundingCheckout } from '@morfi/onramp-sdk/react';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { InformationCircleIcon } from '@/components/ui/icon/glyphs';
import { FundingStatusHeader } from '../history/funding-status-header';
export function FundingConnectionRequired(t0) {
  const $ = _c(51);
  const {
    reason,
    location,
    onRefresh,
    onContinue,
    canAuthenticate: t1,
    busy: t2,
    error: t3
  } = t0;
  const canAuthenticate = t1 === undefined ? false : t1;
  const busy = t2 === undefined ? false : t2;
  const error = t3 === undefined ? false : t3;
  const t = useTranslations("onramp");
  const mismatch = reason === "account_mismatch";
  let t4;
  if ($[0] !== canAuthenticate || $[1] !== location || $[2] !== mismatch || $[3] !== reason || $[4] !== t) {
    const stateName = location?.subdivision === "US-NY" ? t("state_new_york") : location?.subdivision === "US-WA" ? t("state_washington") : location?.subdivision ?? "";
    t4 = !canAuthenticate ? t("no_eligible_methods") : reason === "region" ? location?.country === "US" && location.subdivision ? t("connect_reason_state", {
      state: stateName
    }) : location?.country ? t("connect_reason_country", {
      country: location.country
    }) : t("connect_reason_unknown_location") : mismatch ? t("connect_reason_account_mismatch") : t("connect_reason_provider");
    $[0] = canAuthenticate;
    $[1] = location;
    $[2] = mismatch;
    $[3] = reason;
    $[4] = t;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const message = t4;
  if (reason === "region" && canAuthenticate) {
    let t5;
    if ($[6] !== t) {
      t5 = t("guest_checkout_unavailable");
      $[6] = t;
      $[7] = t5;
    } else {
      t5 = $[7];
    }
    let t6;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <Icon icon={InformationCircleIcon} className="mt-0.5 size-icon-sm shrink-0 text-foreground-secondary" aria-hidden="true" />;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    let t7;
    if ($[9] !== t) {
      t7 = t("guest_checkout_unavailable");
      $[9] = t;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== t7) {
      t8 = <h3 className="text-caption font-medium">{t7}</h3>;
      $[11] = t7;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    let t9;
    if ($[13] !== busy || $[14] !== onRefresh || $[15] !== t) {
      t9 = onRefresh ? <button type="button" className="underline underline-offset-4 hover:text-foreground" disabled={busy} onClick={onRefresh}>{t("refresh_eligibility")}</button> : null;
      $[13] = busy;
      $[14] = onRefresh;
      $[15] = t;
      $[16] = t9;
    } else {
      t9 = $[16];
    }
    let t10;
    if ($[17] !== message || $[18] !== t9) {
      t10 = <p className="text-caption text-foreground-secondary">{message}{" "}{t9}</p>;
      $[17] = message;
      $[18] = t9;
      $[19] = t10;
    } else {
      t10 = $[19];
    }
    let t11;
    if ($[20] !== t10 || $[21] !== t8) {
      t11 = <div className="flex flex-col gap-1">{t8}{t10}</div>;
      $[20] = t10;
      $[21] = t8;
      $[22] = t11;
    } else {
      t11 = $[22];
    }
    let t12;
    if ($[23] !== t11 || $[24] !== t5) {
      t12 = <aside className="flex items-start gap-2 border-t border-border-secondary pt-3" aria-label={t5}>{t6}{t11}</aside>;
      $[23] = t11;
      $[24] = t5;
      $[25] = t12;
    } else {
      t12 = $[25];
    }
    return t12;
  }
  let t5;
  if ($[26] !== canAuthenticate || $[27] !== t) {
    t5 = canAuthenticate ? t("continue_with_moonpay") : t("no_eligible_title");
    $[26] = canAuthenticate;
    $[27] = t;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  let t6;
  if ($[29] !== message || $[30] !== t5) {
    t6 = <FundingStatusHeader artwork="method-expired" title={t5} description={message} />;
    $[29] = message;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  let t7;
  if ($[32] !== busy || $[33] !== canAuthenticate || $[34] !== mismatch || $[35] !== onRefresh || $[36] !== t) {
    t7 = onRefresh && !mismatch ? <AnimatedButton className="h-11 rounded-control" type="button" variant={canAuthenticate ? "secondary" : "primary"} disabled={busy} onClick={onRefresh}>{t("recheck_availability")}</AnimatedButton> : null;
    $[32] = busy;
    $[33] = canAuthenticate;
    $[34] = mismatch;
    $[35] = onRefresh;
    $[36] = t;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== error || $[39] !== t) {
    t8 = error ? <p role="alert" className="text-body">{t("provider_unavailable")}</p> : null;
    $[38] = error;
    $[39] = t;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  let t9;
  if ($[41] !== busy || $[42] !== canAuthenticate || $[43] !== onContinue || $[44] !== t) {
    t9 = canAuthenticate ? <AnimatedButton className="h-11 rounded-control" loading={busy} disabled={busy} onClick={onContinue}>{busy ? t("checking") : t("continue_with_moonpay")}</AnimatedButton> : null;
    $[41] = busy;
    $[42] = canAuthenticate;
    $[43] = onContinue;
    $[44] = t;
    $[45] = t9;
  } else {
    t9 = $[45];
  }
  let t10;
  if ($[46] !== t6 || $[47] !== t7 || $[48] !== t8 || $[49] !== t9) {
    t10 = <div className="flex flex-col gap-4.5">{t6}{t7}{t8}{t9}</div>;
    $[46] = t6;
    $[47] = t7;
    $[48] = t8;
    $[49] = t9;
    $[50] = t10;
  } else {
    t10 = $[50];
  }
  return t10;
}